import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Search as SearchIcon, Plus } from "lucide-react";

const s = (v, fallback = "—") => {
    if (v == null || v === "") return fallback;
    if (typeof v === "string" || typeof v === "number") return v;
    return fallback;
};

const emptyForm = {
    account_id: "",
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    phone: "",
    mobile: "",
    address: "",
};

export default function Contacts() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [q, setQ] = useState("");
    const [accountFilter, setAccountFilter] = useState("");
    const [accounts, setAccounts] = useState([]);

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        setErrored(false);
        try {
            const { data } = await api.get("/crm/contacts", {
                params: { q: q || undefined, accountId: accountFilter || undefined },
            });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setErrored(true);
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const loadAccounts = async () => {
        try {
            const { data } = await api.get("/crm/accounts");
            setAccounts(Array.isArray(data) ? data : []);
        } catch (e) {
            setAccounts([]);
        }
    };

    useEffect(() => {
        load();
        loadAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountFilter]);

    const search = (e) => {
        e.preventDefault();
        load();
    };

    const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const resetForm = () => {
        setForm(emptyForm);
        setShowCreate(false);
    };

    const createContact = async () => {
        if (!form.first_name.trim() && !form.last_name.trim()) {
            toast.error("Provide at least a first or last name.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/crm/contacts", { ...form, account_id: form.account_id || null });
            toast.success("Contact created");
            resetForm();
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const accountName = (accId) => accounts.find((a) => a.id === accId)?.account_name;

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-contacts-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">CRM</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Contacts</h1>
                </div>
                <button onClick={() => setShowCreate(true)} className="h-9 inline-flex items-center gap-1 px-4 rounded bg-foreground text-background text-sm" data-testid="contact-add-button">
                    <Plus className="w-4 h-4" /> New Contact
                </button>
            </div>

            <form onSubmit={search} className="sc-card p-3 flex flex-col md:flex-row gap-3">
                <select className="sc-input md:w-64" value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
                    <option value="">All accounts</option>
                    {accounts.map((a) => (
                        <option key={a.id} value={a.id}>{s(a.account_name)}</option>
                    ))}
                </select>
                <div className="relative flex-1">
                    <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="sc-input pl-9" placeholder="Search name, email…" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
                <button type="submit" className="h-9 px-4 rounded border border-border text-sm hover:bg-secondary">Search</button>
            </form>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[900px]">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Account</th>
                                <th>Title</th>
                                <th>Email</th>
                                <th>Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                            {!loading && errored && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Couldn't load contacts.</td></tr>}
                            {!loading && !errored && rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No contacts found.</td></tr>}
                            {!loading && !errored && rows.map((c) => (
                                <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                                    <td className="font-medium">{`${s(c.first_name, "")} ${s(c.last_name, "")}`.trim() || "—"}</td>
                                    <td className="text-muted-foreground">{s(c.account_name || accountName(c.account_id))}</td>
                                    <td className="text-muted-foreground">{s(c.title)}</td>
                                    <td className="text-xs">{s(c.email)}</td>
                                    <td className="font-mono text-xs">{s(c.phone || c.mobile)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showCreate && (
                <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50" onClick={resetForm}>
                    <div className="sc-card p-5 w-full max-w-xl space-y-4 bg-background" onClick={(e) => e.stopPropagation()} data-testid="contact-create-modal">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Create Contact</div>
                            <div className="text-lg font-semibold">New Contact</div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <Field label="Account" full>
                                <select className="sc-input" value={form.account_id} onChange={(e) => updateForm("account_id", e.target.value)}>
                                    <option value="">No account</option>
                                    {accounts.map((a) => (
                                        <option key={a.id} value={a.id}>{s(a.account_name)}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="First Name"><input className="sc-input" value={form.first_name} onChange={(e) => updateForm("first_name", e.target.value)} /></Field>
                            <Field label="Last Name"><input className="sc-input" value={form.last_name} onChange={(e) => updateForm("last_name", e.target.value)} /></Field>
                            <Field label="Title"><input className="sc-input" value={form.title} onChange={(e) => updateForm("title", e.target.value)} /></Field>
                            <Field label="Email"><input type="email" className="sc-input" value={form.email} onChange={(e) => updateForm("email", e.target.value)} /></Field>
                            <Field label="Phone"><input className="sc-input" value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} /></Field>
                            <Field label="Mobile"><input className="sc-input" value={form.mobile} onChange={(e) => updateForm("mobile", e.target.value)} /></Field>
                            <Field label="Address" full><input className="sc-input" value={form.address} onChange={(e) => updateForm("address", e.target.value)} /></Field>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button onClick={resetForm} className="h-9 px-3 rounded border border-border text-sm">Cancel</button>
                            <button onClick={createContact} disabled={saving} className="h-9 px-4 rounded bg-foreground text-background text-sm disabled:opacity-50" data-testid="contact-save">
                                {saving ? "Creating…" : "Create Contact"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, required = false, full = false, children }) {
    return (
        <label className={`space-y-1 block ${full ? "md:col-span-2" : ""}`}>
            <div className="text-xs font-medium">{label}{required && <span className="text-red-500 ml-1">*</span>}</div>
            {children}
        </label>
    );
}
